import React, { useEffect, useRef, useState } from 'react';
import { cn } from '@/helpers/utils';

const DEFAULT_MAX_LINES = 3;

type ExpandableTextProps = {
  id?: string;
  text: string;
  className?: string;
  maxLines?: number;
  showMoreLabel?: string;
  showLessLabel?: string;
};

const ExpandableText = ({
  id,
  text,
  className,
  maxLines = DEFAULT_MAX_LINES,
  showMoreLabel = 'Show more',
  showLessLabel = 'Hide more',
}: ExpandableTextProps) => {
  const measureRef = useRef<HTMLSpanElement | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const [collapsedText, setCollapsedText] = useState('');
  const contentId = `${id}-content`;

  useEffect(() => {
    const el = measureRef.current;
    if (!el) return undefined;

    const compute = () => {
      // Use the hidden span (with line-clamp) to check if full text overflows
      el.textContent = text;

      if (el.scrollHeight <= el.clientHeight) {
        setCanExpand(false);
        return;
      }

      // Binary search for the longest substring that fits within maxLines with suffix appended
      const suffix = `... ${showMoreLabel}`;
      let low = 0;
      let high = text.length;
      let bestFit = 0;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        el.textContent = `${text.slice(0, mid).trimEnd()}${suffix}`;

        if (el.scrollHeight <= el.clientHeight) {
          bestFit = mid;
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      // Trim to the nearest word boundary to avoid cutting mid-word
      const sliced = text.slice(0, bestFit);
      const lastSpace = sliced.lastIndexOf(' ');
      const cut =
        lastSpace > bestFit * 0.6 ? sliced.slice(0, lastSpace) : sliced;

      setCanExpand(true);
      // Remove trailing punctuation/space so the appended ellipsis reads cleanly.
      setCollapsedText(cut.trimEnd().replace(/[.,;:!? ]+$/u, ''));
    };

    // Collapse back whenever the underlying text (or layout inputs) change
    setExpanded(false);
    compute();

    // Recompute truncation when container width changes (e.g. viewport resize)
    const observer = new ResizeObserver(compute);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, maxLines, showMoreLabel]);

  if (!text) return null;

  return (
    <p id={id} className={cn('tw-relative', className)}>
      {/* Invisible span matching paragraph width — used for overflow measurement via scrollHeight */}
      <span
        ref={measureRef}
        aria-hidden="true"
        className="tw-pointer-events-none tw-invisible tw-absolute tw-left-0 tw-right-0"
        style={{
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      />
      <span id={contentId}>
        {expanded || !canExpand ? text : `${collapsedText}...`}
      </span>
      {canExpand && (
        <>
          {' '}
          <button
            type="button"
            className="tw-inline tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-align-baseline tw-text-blue-bright-600 tw-underline focus_tw-shadow-none focus_tw-outline-none focus_tw-ring-0 focus-visible_tw-shadow-none focus-visible_tw-outline-none focus-visible_tw-ring-0"
            aria-controls={contentId}
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? showLessLabel : showMoreLabel}
          </button>
        </>
      )}
    </p>
  );
};

export default ExpandableText;
